import admin from '@carsfinders/i18n/uk/admin.json';
import adminFinance from '@carsfinders/i18n/uk/adminFinance.json';
import adminMobileDevices from '@carsfinders/i18n/uk/adminMobileDevices.json';
import auctionSchedule from '@carsfinders/i18n/uk/auctionSchedule.json';
import auth from '@carsfinders/i18n/uk/auth.json';
import bidDetails from '@carsfinders/i18n/uk/bidDetails.json';
import bids from '@carsfinders/i18n/uk/bids.json';
import cars from '@carsfinders/i18n/uk/cars.json';
import clients from '@carsfinders/i18n/uk/clients.json';
import common from '@carsfinders/i18n/uk/common.json';
import dealer from '@carsfinders/i18n/uk/dealer.json';
import errors from '@carsfinders/i18n/uk/errors.json';
import feedKeys from '@carsfinders/i18n/uk/feedKeys.json';
import finances from '@carsfinders/i18n/uk/finances.json';
import header from '@carsfinders/i18n/uk/header.json';
import marketplace from '@carsfinders/i18n/uk/marketplace.json';
import messages from '@carsfinders/i18n/uk/messages.json';
import notifications from '@carsfinders/i18n/uk/notifications.json';
import profile from '@carsfinders/i18n/uk/profile.json';
import tabs from '@carsfinders/i18n/uk/tabs.json';
import verification from '@carsfinders/i18n/uk/verification.json';
import wonCars from '@carsfinders/i18n/uk/wonCars.json';

/**
 * Admin i18n — Ukrainian only (the SPA has no locale switcher yet). Mirrors the
 * web `useTranslations().t` contract: namespaces merged under their file name,
 * dotted-key lookup, `{{param}}` / `{param}` interpolation, and the raw key
 * echoed back when a lookup misses (so a missing key is visible, never a crash).
 *
 * It was English until the Design D artboards landed. They are authored in
 * Ukrainian — the operators' own language — and text is geometry: a screen
 * rendered in English cannot be measured against a Ukrainian artboard, because
 * every string has a different width. The `en` catalogues stay in the package
 * (the parity check enforces both), so a locale switcher is a later flip here,
 * not a re-translation.
 */
const messagesTree: Record<string, unknown> = {
  admin,
  adminFinance,
  adminMobileDevices,
  auctionSchedule,
  auth,
  bidDetails,
  bids,
  cars,
  clients,
  common,
  dealer,
  errors,
  feedKeys,
  finances,
  header,
  marketplace,
  messages,
  notifications,
  profile,
  tabs,
  verification,
  wonCars,
};

export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.');
  let value: unknown = messagesTree;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return key;
    }
  }

  if (typeof value !== 'string') return key;

  if (params) {
    return value.replace(/\{\{?(\w+)\}?\}/g, (match, paramKey: string) =>
      params[paramKey] !== undefined ? String(params[paramKey]) : match,
    );
  }

  return value;
}

/** Hook-shaped shim so ported web components keep `const { t } = useTranslations()`. */
export function useTranslations(): { t: typeof t } {
  return { t };
}
